import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Flex,
  Grid,
  Form,
  Picker,
  TextArea,
  ActionButton,
  StatusLight,
  TextField,
  ProgressCircle,
  Slider,
  Item,
  Text,
  View
} from '@adobe/react-spectrum';
import jobManager from '../jobManager';
import { useJobContext } from '../context/JobContext';

import allActions from '../config.json';
import actionWebInvoke, { getHeaders } from '../utils';

export const Voxify = ({ ims }) => {

  //const navigate = useNavigate(); // Replacing useHistory with useNavigate
  const { selectedJob, setSelectedJob } = useJobContext();
  const [nickname, setNickname] = useState('');
  const [gender, setGender] = useState('male');
  const [mode, setMode] = useState('text');
  const [text, setText] = useState('');
  const [state, setState] = useState({
    actionInvokeInProgress: false,
    actionResult: ''
  });

  useEffect(() => {
    if (selectedJob) {
      const job = jobManager.getJob(selectedJob);
      setNickname(job.nickname);
      setGender(job.gender);
      setText(job.text);
      setSpeechRate(job.speechRate || 1);
    }
  }, [selectedJob]);

  const [downloadUrls, setDownloadUrls] = useState([]);

  // Initialize form state from selected job if available
  const [inputText, setInputText] = useState(selectedJob?.text || '');
  const [selectedGender, setSelectedGender] = useState(selectedJob?.gender || 'male');
  const [speechRate, setSpeechRate] = useState(selectedJob?.speechRate || 1);

  // Clear the selected job when the form is submitted
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Your existing submit code

    // Clear the selected job after submission
    setSelectedJob(null);
  };

  // You might want to add a "Clear Form" button
  const handleClear = () => {
    setInputText('');
    setSelectedGender('male');
    setSelectedJob(null);
    setSpeechRate(1);
  };

  // Function to invoke backend action
  async function invokeAction() {
    let oktaAccessToken = 'fake-token';
    setState({ ...state, actionInvokeInProgress: true, actionResult: 'Calling action ... ' });

    const params = {
      gender,
      'custom_speech_rate': speechRate,
      // Always use text
      text: mode === 'text' ? text : text
    };

    try {
      // Assuming actions and headers are defined properly
      const createResponse = await actionWebInvoke(
        allActions['create'],
        getHeaders(ims || {}, oktaAccessToken),
        params);

      // create hash of text + gender + time 
      jobManager.addJob(nickname, createResponse.jobs, text, gender, speechRate);

      setState({
        ...state,
        actionInvokeInProgress: false,
        actionResult: JSON.stringify(createResponse, null, 2)
      });

      jobManager.syncJobs();

      // Navigate to a results page after successful invocation (update path accordingly)
    } catch (e) {
      console.error('Error invoking action:', e);
      setState({
        ...state,
        actionInvokeInProgress: false,
        actionResponseError: true
      });
    }
  }



  async function refreshToken(oktaAuth) {
    try {
      const tokenManager = oktaAuth.tokenManager;
      // Renew the token
      const token = await tokenManager.renew('accessToken');
      return token.accessToken;
    } catch (error) {
      console.error('Error refreshing token:', error);
    }
  }

  return (
    <Form necessityIndicator="icon">
      <Grid
        areas={[
          'nickname gender',
          'empty speechrate',
          'text scratch',
          'button button'
        ]}
        columns={['2fr', '1fr']}
        rows={['auto']}
        gap='size-500'
      >
        <TextField
          gridArea="nickname"
          label="Nickname"
          width="100%"
          value={nickname}
          maxLength={20}
          isRequired={true}
          placeholder="This will help you find the job later!"
          onChange={setNickname} />

        <Picker
          gridArea="gender"
          label="Voice gender"
          width="100%"
          isRequired={true}
          selectedKey={gender}
          placeholder="Select a gender for the voice"
          aria-label="Select a gender for the voice"
          onSelectionChange={setGender}
        >
          <Item key="male">Male</Item>
          <Item key="female">Female</Item>
        </Picker>


        <Slider 
          gridArea={"speechrate"}
          width={'100%'}
          label="Speech rate" value={speechRate} onChange={setSpeechRate} minValue={0.5} maxValue={2} step={0.1} />
          

        <TextArea
          gridArea={"text"}
          label="Text to convert to audio"
          height="30em"
          width="100%"
          isRequired={true}
          value={text}
          placeholder='Enter text to convert to audio'
          onChange={setText} />

        <TextArea
          gridArea={"scratch"}
          label="Scratch pad"
          height="30em"
          width="100%"
          placeholder='Just a place to put text while you work'
        />

        <Flex gridArea={"button"}>
          <ActionButton
            variant="primary"
            type="button"
            onPress={invokeAction}
            isDisabled={!text}
          >
            <Text>Voxify</Text>
          </ActionButton>

          <ProgressCircle
            aria-label="loading"
            isIndeterminate
            isHidden={!state.actionInvokeInProgress}
            marginStart="size-100"
          />
        </Flex>

        {state.actionResponseError && (
          <View padding="size-100" marginTop="size-100" marginBottom="size-100" borderRadius="small">
            <StatusLight variant="negative">
              Failure! See the complete error in your browser console.
            </StatusLight>
          </View>
        )}

        {!state.actionResponseError && state.actionResponse && (
          <View padding="size-100" marginTop="size-100" marginBottom="size-100" borderRadius="small">
            <StatusLight variant="positive">
              Success! See the complete response in your browser console.
            </StatusLight>
          </View>
        )}
      </Grid>
    </Form>
  );
}

Voxify.propTypes = {
  oktaAuth: PropTypes.any,
  runtime: PropTypes.any,
  ims: PropTypes.any
};




